import React, { useState, useEffect } from "react";
import {
    StriimInputField,
    StriimRadio,
    StriimTypography,
    StriimRadioGroup,
    StriimControlLabel,
    StriimCircularLoader
} from "@striim/striim-ui";
import { Box, Grid, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { checkIsCDB, fetchPdbList } from "../../select-tables.utils";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";

interface SelectPdbProps {
    pdbList: string[];
    selectedPdb: string | null;
    onSelectPdb: (pdb: string) => void;
}

const sxStyles = {
    headingContainer: {
        borderBottom: ({ palette }) => `1px solid ${palette.greyscale[200]}`
    },
    searchContainer: {
        width: "350px",
        mt: 1
    },
    childContainer: {
        minHeight: 400,
        maxHeight: 570,
        overflow: "auto"
    }
};

const SelectPdb: React.FC<SelectPdbProps> = ({ pdbList, selectedPdb, onSelectPdb }) => {
    const [data, setData] = useState<string[]>(pdbList);
    const [searchText, setSearchText] = useState<string>("");

    const setSearchData = (searchValue: string) => {
        setSearchText(searchValue || "");
        if (!searchValue) {
            setData(pdbList);
            return;
        }
        const filteredData = pdbList.filter(option => option?.toLowerCase()?.includes(searchValue?.toLowerCase()));
        setData(filteredData);
    };

    return (
        <Box>
            <Box p={3} sx={sxStyles.headingContainer}>
                <Grid container gap={1} direction="column">
                    <StriimTypography variant="h2" sx={{ color: "primary.700" }}>
                        Select a Pluggable Database to Read
                    </StriimTypography>
                    <StriimTypography variant="body4" mb={1} sx={{ color: "greyscale.700" }}>
                        Striim detected the following pluggable databases in your Source.
                        <br /> Select one to read from.
                    </StriimTypography>
                </Grid>
                <Box sx={sxStyles.searchContainer}>
                    <StriimInputField
                        placeholder="Search"
                        onChange={setSearchData}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ width: "8px" }}>
                                    <SearchIcon opacity={0.45} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
            </Box>
            <Box p={3} sx={sxStyles.childContainer}>
                {data && data.length === 0 && searchText ? (
                    <StriimTypography variant="body4" sx={{ color: "greyscale.600" }}>
                        No databases matching "{searchText}"
                    </StriimTypography>
                ) : (
                    <StriimRadioGroup
                        row={false}
                        required
                        value={selectedPdb}
                        onChange={event => {
                            onSelectPdb(event.target.value);
                        }}
                        name="pdb"
                    >
                        {data.map((item, key) => (
                            <StriimControlLabel
                                sx={{ mt: 1 }}
                                key={key}
                                value={item}
                                label={
                                    <StriimTypography variant="body4" sx={{ color: "greyscale.700" }}>
                                        {item}
                                    </StriimTypography>
                                }
                                control={<StriimRadio checked={selectedPdb === item} />}
                            />
                        ))}
                    </StriimRadioGroup>
                )}
            </Box>
        </Box>
    );
};

interface SelectPdbContainerProps {
    formValues: Object;
    dbType: string;
    metaObjectId?: string;
    adapterName: string;
    selectedPdb: string | null;
    cachedPdbList: string[];
    onSelectPdb: (pdb: string) => void;
    onPdbCheckComplete: (isPdb: boolean, pdbList: string[]) => void;
    onError: (error: string) => void;
}

const SelectPdbContainer: React.FC<SelectPdbContainerProps> = ({
    formValues,
    dbType,
    metaObjectId,
    adapterName,
    selectedPdb,
    cachedPdbList,
    onSelectPdb,
    onPdbCheckComplete,
    onError
}) => {
    const [isLoading, setIsLoading] = useState(!cachedPdbList.length);
    const [pdbList, setPdbList] = useState<string[]>(cachedPdbList);

    useEffect(() => {
        if (cachedPdbList.length) {
            return;
        }

        (async () => {
            try {
                setIsLoading(true);
                let formattedProperties = {};
                if (formValues) {
                    for (let key in formValues) {
                        if (Object.prototype.hasOwnProperty.call(formValues, key)) {
                            formattedProperties[key.toLowerCase()] = formValues[key];
                        }
                    }
                }

                const isCdb = await checkIsCDB(formattedProperties, dbType, metaObjectId, adapterName);
                if (!isCdb) {
                    onPdbCheckComplete(false, []);
                    return;
                }

                const pdbs = await fetchPdbList(formattedProperties, dbType, metaObjectId, adapterName);
                if (!pdbs || pdbs.length === 0) {
                    onPdbCheckComplete(false, []);
                    return;
                }

                setPdbList(pdbs);
                onPdbCheckComplete(true, pdbs);
            } catch (error) {
                console.error("Error fetching PDB list:", error);
                onError("Failed to fetch pluggable databases. Please check your connection settings.");
            } finally {
                setIsLoading(false);
            }
        })();
    }, [formValues, cachedPdbList]);

    if (isLoading) {
        return (
            <Grid
                container
                width="100%"
                height="100%"
                minHeight={500}
                alignItems="center"
                justifyContent="center"
                direction="column"
                gap={1}
            >
                <img src="app/components/appwizard/images/get_pdb_loading.svg" />
                <Grid container justifyContent={"center"} alignItems={"center"} gap={0.5}>
                    <Grid item display="flex" alignItems="center" gap={1}>
                        <StriimCircularLoader />
                    </Grid>
                    <StriimTypography variant="body1">
                        Connecting to {DB_PROVIDERS[dbType]?.name || dbType}
                    </StriimTypography>
                </Grid>
            </Grid>
        );
    }

    return <SelectPdb pdbList={pdbList} selectedPdb={selectedPdb} onSelectPdb={onSelectPdb} />;
};

export { SelectPdb, SelectPdbContainer };
